import { NavLink, Outlet } from 'react-router-dom'
import {
  LayoutDashboard,
  Wallet,
  ArrowLeftRight,
  CreditCard,
  Repeat,
  Target,
  Scale,
  LineChart,
  Settings,
  Menu,
  ArrowUpDown,
  GripVertical,
  Check,
  LogOut,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { ThemeToggle } from './ThemeToggle'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useHousehold } from '@/context/HouseholdContext'
import { useAuth } from '@/context/AuthContext'
import { useKeepSnapshot } from '@/hooks/useSnapshots'
import { useT } from '@/i18n/I18nProvider'

const NAV = [
  { to: '/', labelKey: 'nav.dashboard', icon: LayoutDashboard, end: true },
  { to: '/contas', labelKey: 'nav.accounts', icon: Wallet, end: false },
  { to: '/transacoes', labelKey: 'nav.transactions', icon: ArrowLeftRight, end: false },
  { to: '/cartoes', labelKey: 'nav.cards', icon: CreditCard, end: false },
  { to: '/recorrentes', labelKey: 'nav.recurring', icon: Repeat, end: false },
  { to: '/orcamento', labelKey: 'nav.budget', icon: Target, end: false },
  { to: '/patrimonio', labelKey: 'nav.networth', icon: Scale, end: false },
  { to: '/relatorios', labelKey: 'nav.reports', icon: LineChart, end: false },
  { to: '/configuracoes', labelKey: 'nav.settings', icon: Settings, end: false },
]

const ORDER_KEY = 'easymoney.nav-order'

// Aplica a ordem salva; itens novos (sem ordem salva) vão para o fim.
function orderedNav(order: string[]) {
  const byTo = new Map(NAV.map((n) => [n.to, n]))
  const result = order.map((to) => byTo.get(to)).filter(Boolean) as typeof NAV
  for (const n of NAV) if (!order.includes(n.to)) result.push(n)
  return result
}

export function AppLayout() {
  const { household, availableHouseholds, householdId, setActiveHousehold } = useHousehold()
  const { email, signOut } = useAuth()
  // Switcher só quando a pessoa pertence a >1 tenant (multi-household). O super-admin
  // NÃO usa isso para bisbilhotar outros tenants — ele só gere usuários, sem ver dados.
  const multiHousehold = availableHouseholds.length > 1
  const { t } = useT()
  // Mantém a fotografia do mês corrente sempre fresca (ver useSnapshots).
  useKeepSnapshot()
  const [open, setOpen] = useState(false)

  // Ordem do menu (persistida) + modo reordenar.
  const [order, setOrder] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem(ORDER_KEY)
      if (saved) return JSON.parse(saved) as string[]
    } catch {
      /* ignore */
    }
    return NAV.map((n) => n.to)
  })
  const [reorder, setReorder] = useState(false)
  const dragFrom = useRef<number | null>(null)

  useEffect(() => {
    localStorage.setItem(ORDER_KEY, JSON.stringify(order))
  }, [order])

  const items = orderedNav(order)

  function move(from: number, to: number) {
    if (from === to) return
    setOrder(() => {
      const list = items.map((i) => i.to)
      const [moved] = list.splice(from, 1)
      list.splice(to, 0, moved)
      return list
    })
  }

  // Recorrentes NÃO são mais materializadas automaticamente — são incluídas sob
  // demanda pelo botão "Incluir recorrente" em Transações/Cartões/Contas.

  return (
    <div className="min-h-svh md:grid md:grid-cols-[240px_1fr]">
      {/* Sidebar */}
      <aside
        className={cn(
          'border-border bg-card fixed inset-y-0 left-0 z-40 flex w-60 flex-col border-r p-4 transition-transform md:static md:translate-x-0',
          open ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <div className="mb-6 flex items-center gap-2 px-2">
          <div className="bg-primary text-primary-foreground flex size-8 items-center justify-center rounded-lg font-bold">
            €
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-semibold leading-tight">EasyMoney</div>
            {!multiHousehold && (
              <div className="text-muted-foreground truncate text-xs">{household?.name ?? '—'}</div>
            )}
          </div>
          <button
            onClick={() => setReorder((v) => !v)}
            className={cn(
              'rounded-md p-1.5 transition-colors',
              reorder ? 'bg-primary/15 text-primary' : 'text-muted-foreground hover:bg-accent',
            )}
            aria-label={reorder ? t('app.done') : t('app.reorderMenu')}
            title={reorder ? t('app.done') : t('app.reorderMenu')}
          >
            {reorder ? <Check className="size-4" /> : <ArrowUpDown className="size-4" />}
          </button>
        </div>

        {/* Multi-household: alterna o TENANT ativo (as queries são chaveadas por householdId). */}
        {multiHousehold && (
          <div className="mb-4 px-1">
            <Select value={householdId} onValueChange={setActiveHousehold}>
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {availableHouseholds.map((h) => (
                  <SelectItem key={h.id} value={h.id} className="text-xs">
                    {h.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <nav className="flex flex-1 flex-col gap-1">
          {items.map((item, index) =>
            reorder ? (
              <div
                key={item.to}
                draggable
                onDragStart={() => (dragFrom.current = index)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => {
                  if (dragFrom.current !== null) move(dragFrom.current, index)
                  dragFrom.current = null
                }}
                className="border-border/60 bg-accent/40 flex cursor-grab items-center gap-2 rounded-md border border-dashed px-3 py-2 text-sm font-medium active:cursor-grabbing"
              >
                <GripVertical className="text-muted-foreground size-4" />
                <item.icon className="size-4" />
                {t(item.labelKey)}
              </div>
            ) : (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-primary/10 text-primary'
                      : 'text-muted-foreground hover:bg-accent hover:text-foreground',
                  )
                }
              >
                <item.icon className="size-4" />
                {t(item.labelKey)}
              </NavLink>
            ),
          )}
          {reorder && (
            <p className="text-muted-foreground mt-2 px-2 text-xs">{t('app.reorderHint')}</p>
          )}
        </nav>

        <div className="border-border mt-4 space-y-2 border-t pt-3">
          <div className="text-muted-foreground truncate px-1 text-xs" title={email ?? ''}>
            {email}
          </div>
          <button
            onClick={() => void signOut()}
            className="text-muted-foreground hover:bg-accent hover:text-foreground flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors"
          >
            <LogOut className="size-4" />
            {t('app.signOut')}
          </button>
        </div>
      </aside>

      {open && <div className="fixed inset-0 z-30 bg-black/40 md:hidden" onClick={() => setOpen(false)} />}

      {/* Conteúdo */}
      <div className="flex min-w-0 flex-col">
        <header className="border-border flex h-14 items-center justify-between border-b px-4">
          <button className="md:hidden" onClick={() => setOpen(true)} aria-label={t('app.menu')}>
            <Menu className="size-5" />
          </button>
          <div className="flex-1" />
          <ThemeToggle />
        </header>
        <main className="flex-1 p-4 md:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
